import { create } from 'zustand';

/**
 * Engine and editor log lines shown in the Console panels.
 *
 * Errors and warnings describe the project's *current* problems, not a history:
 * the same problem is one line with a count, and a problem that names a file is
 * dropped when that file changes or is hot-reloaded (the engine reports it
 * again if it is still broken). Info and debug lines are an ordinary log.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  readonly id: number;
  readonly time: number;
  readonly level: LogLevel;
  readonly source: string;
  readonly message: string;
  /** Collapsed repeats of the same line. */
  readonly count: number;
}

const LIMIT = 1000;
let nextId = 1;

const isProblem = (level: LogLevel) => level === 'error' || level === 'warn';

/** Project files a message refers to (`scripts/a.rhai:3:1: ...` → `scripts/a.rhai`). */
export function filesIn(message: string): string[] {
  const found = new Set<string>();
  const re =
    /(?:^|[\s"'`([])((?:[\w.-]+\/)*[\w.-]+\.(?:rhai|json|wgsl|png|jpe?g|webp|glb|gltf|ogg|wav|mp3|ttf|otf))(?=[:\s"'`)\],;]|$)/gi;
  for (const match of message.matchAll(re))
    found.add(match[1]!.replace(/\\/g, '/').replace(/^\.\//, ''));
  return [...found];
}

/** `hot-reload reloaded scripts/a.rhai` and similar: the file was just reloaded. */
function reloadedFile(message: string): string | null {
  const match = /\breloaded\s+(\S+)/i.exec(message);
  return match ? match[1]!.replace(/\\/g, '/') : null;
}

const dropFile = (entries: LogEntry[], path: string): LogEntry[] =>
  entries.filter((e) => !isProblem(e.level) || !filesIn(e.message).includes(path));

/* ------------------------------------------------------------------ */
/* Locations and stack traces in messages                              */

/** A `path:line:col` position in a project file (line / column 1-based; column 0 = unknown). */
export interface SourceLocation {
  path: string;
  line: number;
  column: number;
}

/** One frame of a script stack trace (`  at function (path:line:col)`). */
export interface StackFrame extends SourceLocation {
  function: string;
}

const FILE_EXT = 'rhai|json|wgsl|ts|tsx|js|mjs|rs|glsl|txt|md';
const LOCATION_RE = new RegExp(`((?:[\\w.-]+[\\\\/])*[\\w.-]+\\.(?:${FILE_EXT})):(\\d+)(?::(\\d+))?`, 'gi');
const FRAME_RE = /^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)\s*$/;

const cleanPath = (p: string) => p.replace(/\\/g, '/').replace(/^\.\//, '');

/** Every `file.ext:line(:col)` in a text, in order (line 0 = unknown becomes 1). */
export function locationsIn(text: string): (SourceLocation & { index: number; length: number })[] {
  const out: (SourceLocation & { index: number; length: number })[] = [];
  for (const m of text.matchAll(LOCATION_RE)) {
    out.push({
      path: cleanPath(m[1]!),
      line: Math.max(1, Number(m[2])),
      column: m[3] ? Number(m[3]) : 0,
      index: m.index ?? 0,
      length: m[0].length,
    });
  }
  return out;
}

/**
 * Splits a log message into its text and stack trace: script errors from the
 * engine (prism_script) end with one `  at function (path:line:col)` line
 * per frame, innermost first. `location` is where to jump: the innermost
 * frame, else the first `file:line` in the text.
 */
export function parseLogMessage(message: string): { text: string; stack: StackFrame[]; location: SourceLocation | null } {
  const stack: StackFrame[] = [];
  const text: string[] = [];
  for (const line of message.split('\n')) {
    const m = FRAME_RE.exec(line);
    if (m) stack.push({ function: m[1]!, path: cleanPath(m[2]!), line: Math.max(1, Number(m[3])), column: Number(m[4]) });
    else text.push(line);
  }
  const joined = text.join('\n');
  const first = stack[0] ?? locationsIn(joined)[0] ?? null;
  const location = first ? { path: first.path, line: first.line, column: first.column } : null;
  return { text: joined, stack, location };
}

/* ------------------------------------------------------------------ */
/* Collapse (Unity's Console "Collapse")                               */

/**
 * Identical lines (same level, source and message) anywhere in the log as
 * one row: the first occurrence's position and id, the summed count and the
 * latest time.
 */
export function collapseEntries(entries: readonly LogEntry[]): LogEntry[] {
  const groups = new Map<string, LogEntry>();
  const order: string[] = [];
  for (const e of entries) {
    const key = `${e.level}\u0000${e.source}\u0000${e.message}`;
    const seen = groups.get(key);
    if (seen) groups.set(key, { ...seen, count: seen.count + e.count, time: Math.max(seen.time, e.time) });
    else {
      groups.set(key, e);
      order.push(key);
    }
  }
  return order.map((k) => groups.get(k)!);
}

/* ------------------------------------------------------------------ */
/* Per-user console preferences                                        */

export interface ConsolePrefs {
  levels: Record<LogLevel, boolean>;
  filter: string;
  /** Group identical messages with a count. */
  collapse: boolean;
  /** Pause Play when an error is logged. */
  errorPause: boolean;
  /** Clear the log when Play starts. */
  clearOnPlay: boolean;
  /** Clear the log when a build starts (`prism:build-start`). */
  clearOnBuild: boolean;
}

export const CONSOLE_PREFS_KEY = 'ai-studio:console';

export const DEFAULT_CONSOLE_PREFS: ConsolePrefs = {
  levels: { debug: false, info: true, warn: true, error: true },
  filter: '',
  collapse: false,
  errorPause: false,
  clearOnPlay: false,
  clearOnBuild: false,
};

/** Preferences from their stored JSON (anything missing or malformed falls back to the defaults). */
export function parseConsolePrefs(raw: string | null): ConsolePrefs {
  let v: Record<string, unknown> = {};
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) v = parsed as Record<string, unknown>;
  } catch {
    // Unreadable: defaults.
  }
  const d = DEFAULT_CONSOLE_PREFS;
  const bool = (k: keyof ConsolePrefs) => (typeof v[k] === 'boolean' ? (v[k] as boolean) : (d[k] as boolean));
  const lv = v.levels && typeof v.levels === 'object' ? (v.levels as Record<string, unknown>) : {};
  const levels = Object.fromEntries(
    (Object.keys(d.levels) as LogLevel[]).map((l) => [l, typeof lv[l] === 'boolean' ? (lv[l] as boolean) : d.levels[l]]),
  ) as Record<LogLevel, boolean>;
  return {
    levels,
    filter: typeof v.filter === 'string' ? v.filter : d.filter,
    collapse: bool('collapse'),
    errorPause: bool('errorPause'),
    clearOnPlay: bool('clearOnPlay'),
    clearOnBuild: bool('clearOnBuild'),
  };
}

function readPrefs(): ConsolePrefs {
  try {
    return parseConsolePrefs(typeof localStorage === 'undefined' ? null : localStorage.getItem(CONSOLE_PREFS_KEY));
  } catch {
    return parseConsolePrefs(null);
  }
}

function writePrefs(prefs: ConsolePrefs): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(CONSOLE_PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Storage blocked: the settings last for this session.
  }
}

interface ConsoleState {
  projectId: number | null;
  setProject: (projectId: number | null) => void;
  entries: LogEntry[];
  /** Filters and toggles, saved per user in localStorage. */
  prefs: ConsolePrefs;
  setPrefs: (patch: Partial<ConsolePrefs>) => void;
  push: (level: LogLevel, source: string, message: string) => void;
  /** A file changed or reloaded: its errors and warnings are no longer current. */
  resolveFile: (path: string) => void;
  /** Drop every error and warning whose source matches (e.g. a new Play run). */
  resolveSource: (match: (source: string) => boolean) => void;
  clear: () => void;
}

export const useConsole = create<ConsoleState>((set) => ({
  projectId: null,
  setProject: (projectId) =>
    set((state) => (state.projectId === projectId ? state : { projectId, entries: [] })),
  entries: [],
  prefs: readPrefs(),
  setPrefs: (patch) =>
    set((state) => {
      const prefs = { ...state.prefs, ...patch, levels: { ...state.prefs.levels, ...(patch.levels ?? {}) } };
      writePrefs(prefs);
      return { prefs };
    }),
  push: (level, source, message) =>
    set((state) => {
      let entries = state.entries;
      const reloaded = reloadedFile(message);
      if (reloaded && !isProblem(level)) entries = dropFile(entries, reloaded);

      const last = entries.at(-1);
      if (last && last.level === level && last.source === source && last.message === message) {
        const next = entries.slice(0, -1);
        next.push({ ...last, count: last.count + 1, time: Date.now() });
        return { entries: next };
      }
      // A problem already listed is the same problem: count it, move it to the end.
      if (isProblem(level)) {
        const index = entries.findIndex(
          (e) => e.level === level && e.source === source && e.message === message,
        );
        if (index >= 0) {
          const existing = entries[index]!;
          const next = entries.filter((_, i) => i !== index);
          next.push({ ...existing, count: existing.count + 1, time: Date.now() });
          return { entries: next };
        }
      }
      const next = [
        ...entries,
        { id: nextId++, time: Date.now(), level, source, message, count: 1 },
      ];
      return { entries: next.length > LIMIT ? next.slice(-LIMIT) : next };
    }),
  resolveFile: (path) =>
    set((state) => {
      const entries = dropFile(state.entries, path.replace(/\\/g, '/'));
      return entries.length === state.entries.length ? state : { entries };
    }),
  resolveSource: (match) =>
    set((state) => {
      const entries = state.entries.filter((e) => !isProblem(e.level) || !match(e.source));
      return entries.length === state.entries.length ? state : { entries };
    }),
  clear: () => set({ entries: [] }),
}));

/** Play-mode engine messages: tagged so a new run can drop the last run's problems. */
export const isPlaySource = (source: string) =>
  source === 'engine:play' || source.endsWith(' (play)');

export function logEditor(level: LogLevel, message: string): void {
  useConsole.getState().push(level, 'editor', message);
}
